import { ref, render } from 'vue'
import FeatureLayer from '@arcgis/core/layers/FeatureLayer'
import GroupLayer from '@arcgis/core/layers/GroupLayer'
import classBreaks from '@arcgis/core/smartMapping/statistics/classBreaks.js'
import esriConfig from '@arcgis/core/config.js'
import {
  NO_ACCESS_COLOR,
  ROAD_FREQUENCY_BREAK_COLORS,
  DESTINATIONS_FREQUENCY_BREAK_COLORS,
  NO_ACCESS_ROAD_FREQUENCY_BREAK_COLORS,
  NO_ACCESS_DESTINATIONS_FREQUENCY_BREAK_COLORS,
  CLASS_BREAK_COLORS,
  CONNECTIVITY_TRAVEL_LENGTH_BREAK_COLORS,
  NO_ACCESS_CONNECTIVITY_TRAVEL_LENGTH_BREAK_COLORS
} from '@/components/map/constants/arcgisMap'
import { useApi } from '@/api/useApi'
import { useDjangoAuthStore } from '@/stores/authDjango'
import type {
  arcgisMap,
  arcgisSwipe,
  arcgisExpand,
  arcgisLegend
} from '@/components/map/type/arcgis.type'

export function useArcGISMap() {
  const layerLeftTool = ref<arcgisExpand | null>(null)
  const leftLegendTool = ref<arcgisLegend | null>(null)
  const layerLeftList = ref<HTMLElement | null>(null)
  const generalTools = ref<arcgisExpand | null>(null)
  const rightLegendTool = ref<arcgisLegend | null>(null)
  const resetViewTool = ref<arcgisExpand | null>(null)
  const printTool = ref<arcgisExpand | null>(null)

  const setupInterceptors = () => {
    const BASE_APP_API_URL = import.meta.env.VITE_APP_API_BASE_URL
    const baseApiUrlPattern = BASE_APP_API_URL.replace(/\/$/, '').replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    )
    const proxyUrlRegExp = new RegExp(`${baseApiUrlPattern}/wps`, 'i')
    const proxyPrintingServiceUrl = new RegExp(`${baseApiUrlPattern}/print-service`, 'i')

    if (esriConfig.request && Array.isArray(esriConfig.request.interceptors)) {
      esriConfig.request.interceptors.push({
        urls: proxyUrlRegExp,
        before: async (params) => {
          await useDjangoAuthStore().reAuthenticate()
          const BEARER_TOKEN = await useApi().getToken()
          params.requestOptions.headers = {
            ...params.requestOptions.headers,
            Authorization: `Bearer ${BEARER_TOKEN}`
          }
        }
      })
      esriConfig.request.interceptors.push({
        urls: proxyPrintingServiceUrl,
        before: async (params) => {
          await useDjangoAuthStore().reAuthenticate()
          const BEARER_TOKEN = await useApi().getToken()
          params.requestOptions.headers = {
            ...params.requestOptions.headers,
            Authorization: `Bearer ${BEARER_TOKEN}`
          }
        }
      })
    }
  }

  const updateFeatureLayerTitleWithLogic = (featureLayer: FeatureLayer, nameParams: string) => {
    if (featureLayer && featureLayer.title && nameParams) {
      let newTitle = featureLayer.title

      if (newTitle.includes('origin')) {
        newTitle = `${nameParams} Connectivity Travel Length`
      } else if (newTitle.includes('barriers')) {
        newTitle = `${nameParams} Barriers`
      } else if (newTitle.includes('road usage')) {
        newTitle = `${nameParams} Road Frequency Of Usage`
      } else if (newTitle.includes('facility')) {
        newTitle = `${nameParams} Destination Frequency Of Usage`
      } else if (newTitle.includes('Routes')) {
        newTitle = `${nameParams} Disruption`
      }

      featureLayer.title = newTitle
    } else {
      console.error('Invalid FeatureLayer or nameParams provided.')
    }
  }

  const applyCustomLegendBreaksForRoadFrequency = async (
    featureLayer: FeatureLayer,
    fieldForBreaks: string = 'UsageCount'
  ) => {
    try {
      const breaks = await classBreaks({
        layer: featureLayer,
        field: fieldForBreaks,
        classificationMethod: 'natural-breaks',
        numClasses: ROAD_FREQUENCY_BREAK_COLORS.length,
        minValue: 1
      })

      const roundedBreakInfos = breaks.classBreakInfos.map((breakInfo) => ({
        minValue: Math.round(breakInfo.minValue),
        maxValue: Math.round(breakInfo.maxValue),
        label: `${Math.round(breakInfo.minValue)} - ${Math.round(breakInfo.maxValue)}`
      }))

      const noAccessClassBreakInfo = {
        minValue: 0,
        maxValue: 0.99,
        symbol: {
          type: 'simple-line' as const,
          color: NO_ACCESS_ROAD_FREQUENCY_BREAK_COLORS,
          width: 2
        },
        label: 'Not Used'
      }

      const normalClassBreakInfos = roundedBreakInfos.map((b, i) => ({
        minValue: b.minValue,
        maxValue: b.maxValue,
        symbol: {
          type: 'simple-line' as const,
          color: ROAD_FREQUENCY_BREAK_COLORS[i] || '#000',
          width: 2,
          style: 'solid' as const,
          outline: {
            color: [200, 200, 200, 1],
            width: 0.5
          }
        },
        label: b.label
      }))

      const combinedClassBreakInfos = [noAccessClassBreakInfo, ...normalClassBreakInfos]

      featureLayer.renderer = {
        type: 'class-breaks',
        field: fieldForBreaks,
        classBreakInfos: combinedClassBreakInfos,
        legendOptions: {
          title: 'Usage Count'
        }
      }
    } catch (error) {
      console.error('Error applying natural breaks:', error)
    }
  }

  const applyCustomLegendBreaksForOrigin = async (
    featureLayer: FeatureLayer,
    fieldForBreaks: string = 'dist_m'
  ) => {
    try {
      let breakFieldTitle = fieldForBreaks
      if (breakFieldTitle === 'dist_m') {
        breakFieldTitle = 'Travel Distance (m)'
      } else {
        breakFieldTitle = 'Travel Time (min)'
      }
      const breaks = await classBreaks({
        layer: featureLayer,
        field: fieldForBreaks,
        classificationMethod: 'natural-breaks',
        numClasses: CONNECTIVITY_TRAVEL_LENGTH_BREAK_COLORS.length,
        minValue: 0
      })

      const roundedBreakInfos = breaks.classBreakInfos.map((breakInfo) => ({
        minValue: Math.round(breakInfo.minValue),
        maxValue: Math.round(breakInfo.maxValue),
        label: `${Math.round(breakInfo.minValue)} - ${Math.round(breakInfo.maxValue)}`
      }))

      const classBreakInfos = [
        {
          minValue: -1,
          maxValue: -1,
          symbol: {
            type: 'simple-marker' as const,
            color: 'black',
            size: 10,
            outline: { color: 'white', width: 1 }
          },
          label: 'Invalid'
        },
        {
          minValue: -2,
          maxValue: -2,
          symbol: {
            type: 'simple-marker' as const,
            color: NO_ACCESS_CONNECTIVITY_TRAVEL_LENGTH_BREAK_COLORS,
            size: 10,
            width: 4,
            outline: { color: 'white', width: 1 }
          },
          label: 'No Access'
        },
        ...roundedBreakInfos.map((b, i) => ({
          minValue: b.minValue,
          maxValue: b.maxValue,
          symbol: {
            type: 'simple-marker' as const,
            color: CONNECTIVITY_TRAVEL_LENGTH_BREAK_COLORS[i] || '#000',
            size: 8,
            outline: { color: 'white', width: 1 }
          },
          label: b.label
        }))
      ]

      featureLayer.renderer = {
        type: 'class-breaks',
        valueExpression: `
    IIF($feature.Valid == 0, -1, 
      IIF($feature.Connected == 0, -2, $feature["${fieldForBreaks}"])
    )
  `,
        classBreakInfos: classBreakInfos,
        legendOptions: {
          title: breakFieldTitle
        }
      }
    } catch (error) {
      console.error('Error applying natural breaks:', error)
    }
  }

  const applyCustomRendererForBarriers = async (featureLayer: FeatureLayer) => {
    try {
      featureLayer.renderer = {
        type: 'simple',
        symbol: {
          type: featureLayer.geometryType === 'polyline' ? 'simple-line' : 'simple-fill',
          color: '#00A2C2',
          width: featureLayer.geometryType === 'polyline' ? 2 : undefined,
          outline:
            featureLayer.geometryType === 'polygon' ? { color: '#00A2C2', width: 1 } : undefined
        }
      }
    } catch (error) {
      console.error('Error applying renderer for barriers:', error)
    }
  }

  const applyCustomLegendBreaksForDestinationFrequency = async (
    featureLayer: FeatureLayer,
    fieldForBreaks: string = 'UsageCount'
  ) => {
    try {
      const breaks = await classBreaks({
        layer: featureLayer,
        field: fieldForBreaks,
        classificationMethod: 'natural-breaks',
        numClasses: DESTINATIONS_FREQUENCY_BREAK_COLORS.length,
        minValue: 1
      })

      const roundedBreakInfos = breaks.classBreakInfos.map((breakInfo) => ({
        minValue: Math.round(breakInfo.minValue),
        maxValue: Math.round(breakInfo.maxValue),
        label: `${Math.round(breakInfo.minValue)} - ${Math.round(breakInfo.maxValue)}`
      }))

      const noAccessClassBreakInfo = {
        minValue: 0,
        maxValue: 0.99,
        symbol: {
          type: 'simple-marker' as const,
          color: NO_ACCESS_DESTINATIONS_FREQUENCY_BREAK_COLORS,
          size: 12,
          outline: { color: 'black', width: 1 },
          style: 'square' as const
        },
        label: 'No Users'
      }

      const normalClassBreakInfos = roundedBreakInfos.map((b, i) => ({
        minValue: b.minValue,
        maxValue: b.maxValue,
        symbol: {
          type: 'simple-marker' as const,
          color: DESTINATIONS_FREQUENCY_BREAK_COLORS[i] || '#000',
          size: 12,
          outline: { color: 'white', width: 1 },
          style: 'square' as const
        },
        label: b.label
      }))

      const combinedClassBreakInfos = [noAccessClassBreakInfo, ...normalClassBreakInfos]
      featureLayer.renderer = {
        type: 'class-breaks',
        field: fieldForBreaks,
        classBreakInfos: combinedClassBreakInfos,
        legendOptions: {
          title: 'Usage Count'
        }
      }
    } catch (error) {
      console.error('Error applying natural breaks:', error)
    }
  }

  const applyDefaultClassBreaksRenderer = async (
    featureLayer: FeatureLayer,
    fieldForBreaks: string
  ) => {
    const breaks = await classBreaks({
      layer: featureLayer,
      field: fieldForBreaks,
      classificationMethod: 'natural-breaks',
      numClasses: 4,
      minValue: 1
    })

    const colors = CLASS_BREAK_COLORS

    const classBreakInfos = [
      {
        minValue: 0,
        maxValue: 0.99,
        symbol:
          featureLayer.geometryType === 'point'
            ? {
                type: 'simple-marker' as const,
                color: NO_ACCESS_COLOR,
                size: 8,
                outline: { color: 'white', width: 1 }
              }
            : featureLayer.geometryType === 'polyline'
              ? {
                  type: 'simple-line' as const,
                  color: NO_ACCESS_COLOR,
                  width: 2
                }
              : {
                  type: 'simple-fill' as const,
                  color: NO_ACCESS_COLOR,
                  outline: { color: 'white', width: 1 }
                },
        label: 'No access'
      },
      ...breaks.classBreakInfos.map((b, i) => ({
        minValue: b.minValue,
        maxValue: b.maxValue,
        symbol:
          featureLayer.geometryType === 'point'
            ? {
                type: 'simple-marker' as const,
                color: colors[i] || '#000',
                size: 8,
                outline: { color: 'white', width: 1 }
              }
            : featureLayer.geometryType === 'polyline'
              ? {
                  type: 'simple-line' as const,
                  color: colors[i] || '#000',
                  width: 2
                }
              : {
                  type: 'simple-fill' as const,
                  color: colors[i] || '#000',
                  outline: { color: 'white', width: 1 }
                },
        label: b.label
      }))
    ]
    featureLayer.renderer = {
      type: 'class-breaks',
      field: fieldForBreaks,
      classBreakInfos: classBreakInfos
    }
  }

  const rendererOtherLayers = (featureLayer: FeatureLayer) => {
    featureLayer.renderer =
      featureLayer.geometryType === 'point'
        ? {
            type: 'simple',
            symbol: {
              type: 'simple-marker',
              color: 'white',
              size: 8,
              outline: { color: 'white', width: 1 }
            }
          }
        : featureLayer.geometryType === 'polyline'
          ? {
              type: 'simple',
              symbol: {
                type: 'simple-line',
                color: 'white',
                width: 2
              }
            }
          : {
              type: 'simple',
              symbol: {
                type: 'simple-fill',
                color: 'white',
                outline: { color: 'white', width: 1 }
              }
            }
  }

  const getFieldForBreaks = (
    featureLayer: FeatureLayer,
    calculationMethod: string = 'Shortest route'
  ): string => {
    if (featureLayer.fields.some((f) => f.name === 'UsageCount')) {
      return 'UsageCount'
    } else if (featureLayer.fields.some((f) => f.name === 'total_trav')) {
      return 'total_trav'
    } else if (featureLayer.fields.some((f) => f.name === 'Total_Time')) {
      return 'Total_Time'
    } else if (
      featureLayer.fields.some((f) => f.name === 'time_min') &&
      featureLayer.fields.some((f) => f.name === 'dist_m')
    ) {
      return calculationMethod === 'Shortest route' ? 'dist_m' : 'time_min'
    }
    return ''
  }

  const createFeatureLayers = async (
    layerUrls: string[],
    nameParams: string,
    calculationMethod: string = 'Shortest route',
    abortSignal?: AbortSignal
  ) => {
    const featureLayers = []

    for (const url of layerUrls) {
      if (abortSignal?.aborted) {
        return []
      }

      try {
        const featureLayer = new FeatureLayer({
          url,
          outFields: ['*']
        })

        updateFeatureLayerTitleWithLogic(featureLayer, nameParams)

        if (featureLayer.title?.includes('Barriers')) {
          featureLayer.legendEnabled = false
          featureLayer.popupEnabled = true
          featureLayer.outFields = []
        }

        const loadPromise = featureLayer.load()

        if (abortSignal) {
          const abortPromise = new Promise<never>((_, reject) => {
            const abortHandler = () => {
              reject(new Error('Layer loading aborted'))
            }

            if (abortSignal.aborted) {
              abortHandler()
              return
            }

            abortSignal.addEventListener('abort', abortHandler, { once: true })
          })

          try {
            await Promise.race([loadPromise, abortPromise])
          } catch (error) {
            if (
              abortSignal.aborted ||
              (typeof error === 'object' &&
                error !== null &&
                'message' in error &&
                typeof (error as any).message === 'string' &&
                (error as any).message.includes('aborted'))
            ) {
              return []
            }
            throw error
          }
        } else {
          await loadPromise
        }

        if (abortSignal?.aborted) {
          return []
        }

        const fieldInfos = featureLayer.fields.map((field) => ({
          fieldName: field.name,
          label: field.alias || field.name,
          visible: true
        }))

        featureLayer.popupTemplate = {
          title: '{Name}',
          content: [
            {
              type: 'fields',
              fieldInfos
            }
          ]
        }

        const fieldForBreaks = getFieldForBreaks(featureLayer, calculationMethod)

        if (abortSignal?.aborted) return []
        try {
          if (featureLayer.title?.includes('Road Frequency Of Usage')) {
            await applyCustomLegendBreaksForRoadFrequency(featureLayer, fieldForBreaks)
          } else if (featureLayer.title?.includes('Destination Frequency Of Usage')) {
            await applyCustomLegendBreaksForDestinationFrequency(featureLayer, fieldForBreaks)
          } else if (featureLayer.title?.includes('Connectivity Travel Length')) {
            await applyCustomLegendBreaksForOrigin(featureLayer, fieldForBreaks)
          } else if (featureLayer.title?.includes('Barriers')) {
            await applyCustomRendererForBarriers(featureLayer)
          } else {
            if (fieldForBreaks) {
              await applyDefaultClassBreaksRenderer(featureLayer, fieldForBreaks)
            } else {
              rendererOtherLayers(featureLayer)
            }
          }
        } catch (rendererError) {
          if (abortSignal?.aborted) {
            return []
          }
          console.warn('Error applying renderer:', rendererError)
          rendererOtherLayers(featureLayer)
        }

        if (!abortSignal?.aborted) {
          featureLayers.push(featureLayer)
        } else {
          return []
        }
      } catch (error) {
        if (abortSignal?.aborted || (error as any)?.message?.includes('aborted')) {
          return []
        }
        console.error(`Error loading FeatureLayer for URL ${url}:`, error)
      }
    }

    return featureLayers
  }

  const addLayersToMap = async (
    mapView: arcgisMap,
    swipeView: arcgisSwipe,
    featureLayers: FeatureLayer[],
    comparisionLayer: FeatureLayer[],
    nameParams: string
  ) => {
    const layerExtents = []
    const allLayers = [...featureLayers, ...comparisionLayer]
    for (const featureLayer of allLayers) {
      try {
        const response = await featureLayer.queryExtent({ where: '1=1' })
        if (response.extent) {
          layerExtents.push(response.extent)
        }
      } catch (error) {
        try {
          const response = await featureLayer.queryExtent({})
          if (response.extent) {
            layerExtents.push(response.extent)
          }
        } catch (innerError) {
          console.warn('Could not query extent for layer:', featureLayer.title, innerError)
          return
        }
      }
    }
    if (comparisionLayer.length > 0) {
      const comparisionGroupLayer = new GroupLayer({
        title: `${nameParams}`,
        layers: comparisionLayer
      })
      mapView.map.add(comparisionGroupLayer)
    } else {
      const allLayersGroup = new GroupLayer({
        title: nameParams,
        layers: allLayers
      })
      mapView.map.add(allLayersGroup)
    }
    if (mapView.ready && comparisionLayer.length > 0) {
      swipeView.leadingLayers = featureLayers
      swipeView.trailingLayers = comparisionLayer
    }

    if (layerExtents.length > 0) {
      const combinedExtent = layerExtents.reduce((acc, extent) => acc.union(extent))
      mapView.goTo(combinedExtent)
    }
  }

  const addFeatureLayerToMap = async (
    mapView: arcgisMap,
    featureLayerList: FeatureLayer[],
    nameParams: string
  ) => {
    const layerExtents = []

    const allLayersGroup = new GroupLayer({
      title: nameParams,
      layers: featureLayerList
    })

    for (const featureLayer of featureLayerList) {
      try {
        const response = await featureLayer.queryExtent({ where: '1=1' })
        if (response.extent) {
          layerExtents.push(response.extent)
        }
      } catch (error) {
        try {
          const response = await featureLayer.queryExtent({})
          if (response.extent) {
            layerExtents.push(response.extent)
          }
        } catch (innerError) {
          console.warn('Could not query extent for layer:', featureLayer.title, innerError)
          return
        }
      }
    }

    mapView.map.add(allLayersGroup)

    if (layerExtents.length > 0) {
      const combinedExtent = layerExtents.reduce((acc, extent) => acc.union(extent))
      mapView.goTo(combinedExtent)
    }
  }

  const addLayersToSwipeView = async (
    featureLayers: FeatureLayer[],
    swipeView: arcgisSwipe,
    comparisionLayer: FeatureLayer[]
  ) => {
    if (swipeView) {
      swipeView.leadingLayers = featureLayers
      swipeView.trailingLayers = comparisionLayer
    }
  }

  const getLayersFromGroup = (mapView: arcgisMap, groupName: string) => {
    const groupLayer = mapView.map.layers.find((layer: any) => layer.title === groupName)
    if (groupLayer && groupLayer instanceof GroupLayer) {
      return groupLayer.layers
    } else {
      console.warn(`GroupLayer with title "${groupName}" not found or is not a GroupLayer.`)
      return []
    }
  }

  const exportLegendBreaks = (featureLayers: FeatureLayer[]) => {
    const legendBreaks = featureLayers
      .map((layer) => {
        if (layer.renderer && layer.renderer.type === 'class-breaks') {
          return {
            layerTitle: layer.title,
            field: layer.renderer.field,
            classBreakInfos: layer.renderer.classBreakInfos.map((info) => ({
              minValue: info.minValue,
              maxValue: info.maxValue,
              symbol: info.symbol,
              label: info.label
            })),
            valueExpression: layer.renderer.valueExpression,
            legendOptions: layer.renderer.legendOptions
          }
        } else {
          console.warn(`Layer "${layer.title}" does not have a class-breaks renderer.`)
          return null
        }
      })
      .filter((breaks) => breaks !== null)
    return legendBreaks
  }

  const applyLegendBreaksToLayers = (legendBreaks: any, featureLayers: FeatureLayer[]) => {
    const legendBreaksItems = legendBreaks.items
    featureLayers.forEach((layer) => {
      const matchingLegend = legendBreaksItems.find((legend: any) => {
        return (
          (layer.title?.includes('Destination Frequency Of Usage') &&
            legend.layerTitle.includes('Destination Frequency Of Usage')) ||
          (layer.title?.includes('Connectivity Travel Length') &&
            legend.layerTitle.includes('Connectivity Travel Length')) ||
          (layer.title?.includes('Disruption') && legend.layerTitle.includes('Disruption')) ||
          (layer.title?.includes('Road Frequency Of Usage') &&
            legend.layerTitle.includes('Road Frequency Of Usage'))
        )
      })

      if (matchingLegend) {
        const classBreakInfos = [...matchingLegend.classBreakInfos]
        const maxBaseValue = Math.max(...classBreakInfos.map((info) => info.maxValue))

        const isDestinationLayer = layer.title?.includes('Destination Frequency Of Usage')

        const originalSymbol = classBreakInfos[0]?.symbol
        const markerStyle = originalSymbol?.style || (isDestinationLayer ? 'square' : 'circle')

        const outOfRangeSymbol = {
          type:
            layer.geometryType === 'point'
              ? 'simple-marker'
              : layer.geometryType === 'polyline'
                ? 'simple-line'
                : 'simple-fill',
          color: '#E60000',
          size: layer.geometryType === 'point' ? (isDestinationLayer ? 12 : 10) : undefined,
          width: layer.geometryType === 'polyline' ? 2 : undefined,
          outline: { color: 'white', width: 1 }
        }

        if (layer.geometryType === 'point' && outOfRangeSymbol.type === 'simple-marker') {
          ;(outOfRangeSymbol as { style?: string }).style = markerStyle
        }

        classBreakInfos.push({
          minValue: maxBaseValue,
          maxValue: Infinity,
          symbol: outOfRangeSymbol,
          label: `Above ${maxBaseValue}`
        })

        layer.renderer = {
          type: 'class-breaks',
          field: matchingLegend.field,
          classBreakInfos: classBreakInfos,
          ...(matchingLegend.valueExpression
            ? { valueExpression: matchingLegend.valueExpression }
            : {}),
          ...(matchingLegend.legendOptions ? { legendOptions: matchingLegend.legendOptions } : {})
        }
      } else {
        console.warn(`No matching legend breaks found for layer "${layer.title}".`)
      }
    })
  }

  const removeGroupLayer = async (mapView: arcgisMap, groupLayerTitle: string) => {
    const groupLayer = mapView.map.layers.find((layer: any) => layer.title === groupLayerTitle)
    if (groupLayer) {
      mapView.map.remove(groupLayer)
    } else {
      console.warn(`GroupLayer "${groupLayerTitle}" not found.`)
    }
  }

  return {
    layerLeftTool,
    leftLegendTool,
    layerLeftList,
    generalTools,
    rightLegendTool,
    resetViewTool,
    printTool,
    setupInterceptors,
    createFeatureLayers,
    addLayersToMap,
    addFeatureLayerToMap,
    addLayersToSwipeView,
    getLayersFromGroup,
    exportLegendBreaks,
    applyLegendBreaksToLayers,
    removeGroupLayer
  }
}
